// « Mes demandes » en calendrier : ce que montre chaque carte d'une case — phrase, qui agit, urgence, ordre, étapes de la frise.
// Fonctions PURES, testées dans elementsJour.test.ts. Mêmes mots que la carte « Prochaine étape » de la fiche (parcours.ts),
// raccourcis pour tenir dans une case ; la table de référence est la planche « Les 21 états » de la maquette (docs/DECISIONS.md §25).
// Aucune règle métier ici : on ne fait que reformuler ce que le serveur renvoie (liste ?vue=miennes) pour la personne absente.
import type { Demande, Statut } from '../../types';
import {
  PHASES_PARCOURS, infoBandeau, phaseCourante, seancePasseeLe, surchargeCarte, type ContexteMessageSlack,
} from './parcours';
import { STATUTS_NON_RESOLUS, niveauUrgence, joursAvant, type NiveauUrgence, type SeuilsUrgence } from './urgence';
import { vueReponse, type VueReponse } from './confirmation';
import { dePersonne } from '../messages/format';

// ── Séance « à couvrir » (aucune demande) ───────────────────────────────────

/** Séance dont le/la titulaire est absent·e ET sans demande de remplacement. */
export interface Trou {
  date: string;
  atelierId: number;
  atelierNom: string;
  poleId: number | null;
  /** Couleur du pôle de l'atelier (point de la carte). */
  poleCouleur: string | null;
  lieu: string | null;
  /** 'HH:MM:SS' de la séance du jour (programme), sinon de l'atelier ; null si inconnue. */
  heureDebut: string | null;
  titulaireId: number;
  titulaireNom: string;
  joursRestants: number;
  niveau: NiveauUrgence;
}

// ── Remplaçant·e en vigueur ─────────────────────────────────────────────────

/** Miroir de server/utils/access.ts::STATUTS_AVEC_REMPLACANT : la désignation n'est « en vigueur » que dans ces états. */
export const STATUTS_AVEC_REMPLACANT: Statut[] = [
  'validation_responsable', 'validation_rh', 'attente_tipee',
  'passation', 'atelier_donne', 'cloturee', 'maintenue',
];

/** La réponse du/de la remplaçant·e, dans les mots de la carte « Prochaine étape » (parcours.ts) : le serveur décide, ici on traduit. */
export function reponseDe(d: Pick<Demande, 'confirmation_remplacant'>): 'confirmee' | 'attente' | 'retard' | 'sans_reponse' | null {
  const c = d.confirmation_remplacant;
  if (!c || c.confirmation == null) return null;
  if (c.sans_reponse_apres_seance) return 'sans_reponse';
  if (c.confirmation === 'confirmee') return 'confirmee';
  return c.en_retard ? 'retard' : 'attente';
}

/** La réponse vue par l'écran (violet / rouge si la séance est dans le seuil « Urgent »). */
export function vueDeLaDemande(d: Pick<Demande, 'confirmation_remplacant' | 'date_atelier'>, seuils: SeuilsUrgence, maintenant: Date = new Date()): VueReponse {
  return vueReponse(d.confirmation_remplacant, d.date_atelier, seuils, maintenant);
}

/** Nom du/de la remplaçant·e SI la désignation est en vigueur — après un renvoi, l'affectation reste en base : on ne montre pas un nom périmé. */
export function remplacantEnVigueur(d: Pick<Demande, 'statut' | 'remplacant_nom'>): string | null {
  return STATUTS_AVEC_REMPLACANT.includes(d.statut) && d.remplacant_nom ? d.remplacant_nom : null;
}

// ── Frise ───────────────────────────────────────────────────────────────────

export interface EtatFrise {
  /** 1..7 ; 8 = tout est fait ; null = hors parcours normal (urgence, refusée, annulée, sans suite…) : ni traits ni nom d'étape. */
  phase: number | null;
  numero: number;
  total: number;
  termine: boolean;
  /** « Recherche »… « Retour » ; null hors parcours ou une fois tout fait. */
  nom: string | null;
  /** Un trait par étape : fait, en cours, à venir (''). Vide hors parcours. L'étape 2 est « sous réserve » (tiretée, rouge si urgent) tant que la réponse manque. */
  traits: ('fait' | 'encours' | 'reserve' | 'reserve urgent' | '')[];
}

/** Où en est la demande, pour la mini-frise de la liste ET la carte du calendrier (une seule règle : phaseCourante). */
export function etatFrise(statut: Statut, dateAtelier?: string | null, maintenant: Date = new Date(), vue: VueReponse = 'aucune'): EtatFrise {
  const total = PHASES_PARCOURS.length;
  const phase = phaseCourante(statut, dateAtelier, maintenant);
  if (phase == null) return { phase: null, numero: 0, total, termine: false, nom: null, traits: [] };
  const termine = phase > total;
  const numero = Math.min(phase, total);
  return {
    phase, numero, total, termine,
    nom: termine ? null : PHASES_PARCOURS[numero - 1],
    traits: PHASES_PARCOURS.map((_, i) => (
      i === 1 && vue !== 'aucune' ? (vue === 'urgent' ? 'reserve urgent' : 'reserve')
        : i + 1 < phase || termine ? 'fait' : i + 1 === phase ? 'encours' : '')),
  };
}

// ── Qui agit ────────────────────────────────────────────────────────────────

/** « À vous de jouer » → « à vous de jouer » (après les deux-points), sans toucher à un nom propre. */
export function enPhrase(t: string): string {
  return /^(À |Suivi|En attente|Menée)/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t;
}

/** Le message Slack de la recherche, au format de la carte « Prochaine étape » (la liste n'en connaît que l'état). */
export function contexteSlack(d: Demande): ContexteMessageSlack | null {
  const s = d.slack_courant;
  if (!s || (d.statut !== 'recherche_interne_1' && d.statut !== 'recherche_interne_2')) return null;
  return { messageId: 0, texte: '', etat: s.etat, relance: s.relance, aCharge: null, peutMarquer: s.peut_marquer };
}

/** La personne peut relancer ses collègues : l'action est offerte ET le délai (48 h par défaut après le 1er envoi) est écoulé — comme la fiche. */
export function relanceDisponible(d: Demande, maintenant: Date = new Date()): boolean {
  return d.statut === 'recherche_interne_1'
    && (d.actions_possibles ?? []).includes('relancer_slack')
    && d.relance_possible_at != null
    && new Date(d.relance_possible_at).getTime() <= maintenant.getTime();
}

/** Séance passée sans remplaçant·e : il n'y a plus rien à faire pour la personne (la gestion classe la demande). */
const PASSEE_SANS_ACTION: Statut[] = ['soumise', 'recherche_interne_1', 'recherche_interne_2', 'recherche_externe'];

/**
 * Qui tient la balle, en quelques mots (« à vous de jouer », « suivi par l'équipe gestion »…) — SOURCE UNIQUE pour la mini-frise
 * de la liste et la carte du calendrier. Vide quand il n'y a rien à dire (terminé, ou séance passée sans remplaçant·e).
 */
export function quiAgit(d: Demande, peutGerer: boolean, maintenant: Date = new Date()): string {
  const reponse = reponseDe(d);
  if (!peutGerer && seancePasseeLe(d.date_atelier, maintenant)) {
    if (PASSEE_SANS_ACTION.includes(d.statut)) return '';
    // Séance passée sans réponse enregistrée : la gestion régularise, la personne n'a rien à faire.
    if (reponse === 'sans_reponse') return "suivi par l'équipe gestion";
    // Après la séance : le retour est transmis → à la personne de le lire ; sinon la gestion le suit.
    if (d.statut === 'atelier_donne') return d.retour_transmis ? 'à vous de jouer' : "suivi par l'équipe gestion";
  }
  const carte = surchargeCarte(d.statut, {
    peutGerer, estAbsent: !peutGerer, absentNom: d.absent_nom ?? 'la personne absente',
    relanceDisponible: relanceDisponible(d, maintenant), relancePossibleLe: null, source: d.source,
    reserveGestion: d.reserve_gestion ?? false, messageSlack: peutGerer ? null : contexteSlack(d),
    remplacantNom: remplacantEnVigueur(d), reponse,
  });
  // Le délai de réponse est écoulé : la personne absente peut relancer ou choisir quelqu'un d'autre — c'est à elle de jouer.
  if (!peutGerer && reponse === 'retard') return 'à vous de jouer';
  if (carte) return enPhrase(carte.quiAgit);
  switch (infoBandeau(d.statut, d.date_atelier, maintenant).acteur) {
    case 'gestion': return "suivi par l'équipe gestion";
    case 'remplacant': return 'en attente de la confirmation du/de la remplaçant·e';
    case 'absent': return peutGerer ? `en attente ${dePersonne(d.absent_nom ?? 'la personne absente')}` : 'à vous de jouer';
    default: return '';
  }
}

export function aVousDeJouer(d: Demande, maintenant: Date = new Date()): boolean {
  return quiAgit(d, false, maintenant).startsWith('à vous de jouer');
}

// ── La même carte, vue par la gestion ───────────────────────────────────────
// La gestion voit les demandes de TOUT LE MONDE : la carte nomme la personne absente (et son/sa remplaçant·e) et la phrase est écrite à la
// 3e personne — les titres de la carte « Prochaine étape » de la fiche, côté gestion (parcours.ts), raccourcis. Même frise, mêmes états,
// mêmes mots « qui agit » que la liste (quiAgit(…, true)) : rien de nouveau à apprendre.

/** Qui regarde : la personne absente (« Mes demandes ») ou la gestion (toutes les demandes). */
export type VoixCarte = 'personne' | 'gestion';

/** « Alexia Sestito » → « Alexia S. » : la place d'une carte est comptée. */
export function nomCourt(nom: string | null | undefined): string {
  const mots = (nom ?? '').trim().split(/\s+/).filter(Boolean);
  if (mots.length < 2) return mots[0] ?? '';
  return `${mots[0]} ${mots[mots.length - 1].charAt(0)}.`;
}

/** Le prénom, pour une phrase (« Alexia cherche un·e remplaçant·e ») ; on nomme toujours la personne, jamais « la personne ». */
export function prenomDe(nom: string | null | undefined): string {
  return (nom ?? '').trim().split(/\s+/)[0] || "L'intervenant·e";
}

/**
 * C'est à la gestion de jouer (et pas seulement de suivre) : la carte le met en gras, comme « à vous de jouer » pour la personne.
 * Quand la recherche est « menée par Alexia » ou qu'on attend le/la remplaçant·e, la balle n'est pas chez la gestion.
 */
export function gestionDoitAgir(d: Demande, maintenant: Date = new Date()): boolean {
  const q = quiAgit(d, true, maintenant);
  return q.startsWith("suivi par l'équipe gestion") || q.startsWith('à vous de jouer') || q.startsWith('Congé Tipee');
}

/**
 * La phrase de la carte pour la gestion. Comme celle de la personne, elle ne dit jamais une chose fausse : elle ne parle pas de l'état du message
 * Slack (la liste ne le dit pas) et, pour une séance passée sans remplaçant·e confirmé·e, elle le dit.
 */
export function phraseCarteGestion(d: Demande, maintenant: Date = new Date()): string {
  const passee = seancePasseeLe(d.date_atelier, maintenant);
  const nom = remplacantEnVigueur(d);
  const absent = prenomDe(d.absent_nom);
  // La séance a eu lieu sans que la réponse ait jamais été enregistrée : c'est ce qu'il faut régulariser, avant tout le reste.
  if (reponseDe(d) === 'sans_reponse') return `Séance passée : confirmation ${nom ? dePersonne(nom) : 'du/de la remplaçant·e'} non enregistrée`;
  switch (d.statut) {
    case 'brouillon':
      if (!d.reserve_gestion) return `Pas encore envoyée par ${absent}`;
      // Réservée à la gestion : atelier en alternance à vérifier (DECISIONS §36), sinon maladie à temps partiel (§12).
      return d.coanimation?.mode === 'alternance' && d.coanimation.autre && !d.coanimation.autre.absent
        ? `Alternance avec ${prenomDe(d.coanimation.autre.nom)} : qui anime ce jour-là ?`
        : 'Maladie à temps partiel : à vous de décider';
    case 'soumise': return passee ? PHRASE_SEANCE_PASSEE : `${absent} cherche un·e remplaçant·e`;
    case 'recherche_interne_1': return passee ? PHRASE_SEANCE_PASSEE : `${absent} attend des réponses au 1er appel Slack`;
    case 'recherche_interne_2': return passee ? PHRASE_SEANCE_PASSEE : 'Second tour Slack en cours';
    case 'recherche_externe': return passee ? PHRASE_SEANCE_PASSEE : 'Recherche élargie au vivier externe';
    case 'fusion_demandee': return 'Fusion avec un autre atelier à l’étude';
    case 'evaluation_annulation': return 'À évaluer : annuler ou maintenir la séance ?';
    case 'urgence_cascade': return 'Urgence : recherche en cascade en cours';
    case 'validation_responsable': return 'En attente de validation du/de la responsable';
    case 'validation_rh': return 'En attente de validation des RH';
    case 'attente_tipee': return `Absence ${dePersonne(absent)} à valider dans Tipee`;
    case 'passation': return nom ? `Passation avec ${nom}` : 'Passation en cours';
    case 'atelier_donne': return passee ? 'Séance passée : retour et présences à contrôler' : 'La séance approche';
    case 'cloturee': return 'Remplacement effectué';
    case 'absence_validee': return 'Absence validée : aucun remplacement nécessaire';
    case 'fusionnee': return 'Séance fusionnée avec un autre atelier';
    case 'maintenue': return 'Séance maintenue';
    case 'annulee': return 'Séance annulée';
    case 'refusee':
      return d.issue_refus === 'absence_refusee_rh' ? 'Absence refusée par les RH'
        : d.issue_refus === 'demande_annulee' ? "Demande annulée par l'équipe gestion"
        : d.issue_refus === 'demande_refusee' ? 'Demande refusée par le/la responsable' : 'Demande refusée';
    case 'sans_suite': return 'Demande classée sans suite';
    default: {
      const oubli: never = d.statut;   // un statut ajouté à la machine à états sans phrase : TypeScript refuse de compiler
      return oubli;
    }
  }
}

/** Le complément de la phrase dans le détail du jour : le sous-titre de la carte « Prochaine étape » de la fiche (côté gestion), tel quel. */
export function complementCarteGestion(d: Demande, maintenant: Date = new Date()): string {
  if (seancePasseeLe(d.date_atelier, maintenant) && PASSEE_SANS_ACTION.includes(d.statut)) return '';
  const carte = surchargeCarte(d.statut, {
    peutGerer: true, estAbsent: false, absentNom: d.absent_nom ?? "L'intervenant·e", relanceDisponible: false, relancePossibleLe: null,
    source: d.source, reserveGestion: d.reserve_gestion ?? false, remplacantNom: remplacantEnVigueur(d), messageSlack: null, reponse: reponseDe(d),
  });
  // Sur la fiche, ces phrases renvoient à ce qui est « ci-dessous » / « ici » : dans le détail du jour, c'est la demande.
  return (carte?.sousTitre ?? infoBandeau(d.statut, d.date_atelier, maintenant).sousTitre ?? '')
    .replace(' ci-dessous', '').replace('confirmez-le ici', 'confirmez-le dans la demande');
}

// ── La phrase de la carte ───────────────────────────────────────────────────

export const PHRASE_SEANCE_PASSEE = 'Séance passée sans remplacement confirmé';

/**
 * Où en est la demande, en une phrase courte, pour la personne absente : les titres de la carte « Prochaine étape » de sa fiche
 * (parcours.ts), raccourcis. Elle ne dit jamais une chose fausse : sans état connu du message Slack, « Recherche lancée :
 * continuez à chercher » plutôt que « vos collègues sont prévenu·e·s » (qui n'est vrai que si le message est publié).
 */
export function phraseCarte(d: Demande, maintenant: Date = new Date()): string {
  const passee = seancePasseeLe(d.date_atelier, maintenant);
  const nom = remplacantEnVigueur(d);
  if (reponseDe(d) === 'sans_reponse') return `Séance passée sans réponse ${nom ? dePersonne(nom) : 'de votre remplaçant·e'}`;
  switch (d.statut) {
    case 'brouillon': return 'Pas encore envoyée';
    case 'soumise': return passee ? PHRASE_SEANCE_PASSEE : 'Trouvez votre remplaçant·e';
    case 'recherche_interne_1':
    case 'recherche_interne_2': {
      if (passee) return PHRASE_SEANCE_PASSEE;
      const s = d.slack_courant ?? null;
      const relance = s?.relance ?? d.statut === 'recherche_interne_2';
      if (s && (s.etat === 'a_envoyer' || s.etat === 'erreur')) {
        if (!s.peut_marquer) return relance ? "L'équipe gestion publie votre relance" : "L'équipe gestion publie votre message";
        if (s.etat === 'erreur') return relance ? "Votre relance n'a pas pu être publiée" : "Votre message n'a pas pu être publié";
        return relance ? 'Publiez votre relance sur Slack' : 'Publiez votre message sur Slack';
      }
      if (s?.etat === 'envoi_en_cours') return relance ? 'Votre relance part sur Slack…' : 'Votre message part sur Slack…';
      if (s?.etat === 'envoye') {
        if (!relance && relanceDisponible(d, maintenant)) return 'Pas encore de réponse ? Relancez vos collègues';
        return relance ? 'Relance publiée sur Slack' : 'Vos collègues sont prévenu·e·s sur Slack';
      }
      return relance ? 'Relance lancée : continuez à chercher' : 'Recherche lancée : continuez à chercher';
    }
    case 'recherche_externe': return passee ? PHRASE_SEANCE_PASSEE : "Continuez à chercher : l'équipe gestion vous aide";
    case 'fusion_demandee':
    case 'evaluation_annulation': return "L'équipe gestion étudie d'autres solutions";
    case 'urgence_cascade': return "L'équipe gestion cherche en urgence";
    case 'validation_responsable': return 'En attente du/de la responsable';
    case 'validation_rh': return 'En attente de validation des RH';
    case 'attente_tipee': return 'Les RH valident votre absence dans Tipee';
    case 'passation': return nom ? `Passation avec ${nom}` : 'Passation en cours';
    case 'atelier_donne': {
      if (!passee) return 'La séance approche';
      const qui = nom ?? 'votre remplaçant·e';
      return d.retour_transmis ? `Retour ${dePersonne(qui)} reçu : à lire` : `Retour ${dePersonne(qui)} attendu`;
    }
    case 'cloturee': return 'Remplacement effectué';
    case 'absence_validee': return 'Aucun remplacement nécessaire';
    case 'fusionnee': return 'Séance fusionnée avec un autre atelier';
    case 'maintenue': return "Séance maintenue par l'équipe gestion";
    case 'annulee': return 'Séance annulée';
    case 'refusee':
      return d.issue_refus === 'absence_refusee_rh' ? 'Absence refusée par les RH'
        : d.issue_refus === 'demande_annulee' ? "Demande annulée par l'équipe gestion"
        : d.issue_refus === 'demande_refusee' ? 'Demande refusée par le/la responsable' : 'Demande refusée';
    case 'sans_suite': return 'Demande écartée : aucun remplacement';
    default: {
      const oubli: never = d.statut;   // un statut ajouté à la machine à états sans phrase : TypeScript refuse de compiler
      return oubli;
    }
  }
}

/** Le complément de la phrase dans le détail du jour : le sous-titre de la carte « Prochaine étape » de la fiche, pour la personne absente. */
export function complementCarte(d: Demande, maintenant: Date = new Date()): string {
  const passee = seancePasseeLe(d.date_atelier, maintenant);
  const s = d.slack_courant ?? null;
  const nom = remplacantEnVigueur(d);
  const reponse = reponseDe(d);
  if (reponse === 'sans_reponse') return 'L’équipe gestion régularise la demande. Vous n’avez rien à faire.';
  switch (d.statut) {
    case 'brouillon': return 'Envoyez-la pour lancer la recherche de remplaçant·e.';
    case 'soumise': return passee ? '' : "Contactez directement un·e collègue, ou prévenez toute l'équipe sur Slack. Dès que quelqu'un accepte, indiquez-le dans la demande.";
    case 'recherche_interne_1':
    case 'recherche_interne_2':
      if (passee) return '';
      if (s && (s.etat === 'a_envoyer' || s.etat === 'erreur') && s.peut_marquer) return "Ouvrez la demande pour copier le message, le publier dans #remplacements, puis indiquer qu'il est publié.";
      if (s && s.etat === 'erreur') return "L'équipe gestion est prévenue. Continuez aussi à chercher de votre côté.";
      return d.statut === 'recherche_interne_2'
        ? "Continuez à chercher de votre côté. Sans réponse, l'équipe gestion viendra vous aider."
        : 'Continuez aussi à chercher de votre côté.';
    case 'recherche_externe': return passee ? '' : "L'équipe gestion vous aide maintenant. Un message direct à un·e collègue aboutit souvent plus vite.";
    case 'fusion_demandee':
    case 'evaluation_annulation': return 'Vous serez prévenu·e de la décision.';
    case 'urgence_cascade': return "L'équipe gestion s'en occupe. Vous verrez ici ce qui a été décidé.";
    case 'validation_responsable':
      return reponse === 'attente' || reponse === 'retard' ? `${nom ?? 'Le/la remplaçant·e'} ${reponse === 'retard' ? 'n’a pas répondu' : 'n’a pas encore confirmé'}. En attendant, votre demande continue.`
        : 'Remplaçant·e confirmé·e. Prochaine étape : validation des RH.';
    case 'attente_tipee':
      return reponse === 'attente' || reponse === 'retard'
        ? `Vous avez déjà fait votre demande dans Tipee : plus rien à faire de votre côté, sauf si ${nom ?? 'le/la remplaçant·e'} décline (la recherche reprendrait).`
        : "Votre remplacement est validé. Vous avez déjà fait votre demande dans Tipee : vous n'avez plus rien à faire.";
    case 'passation':
      return reponse === 'attente' || reponse === 'retard'
        ? `Dites qui débute, écrivez un mot pour chacun·e et le côté pratique. ${nom ?? 'Votre remplaçant·e'} ne le lira qu'après avoir confirmé, puis une fois que vous aurez transmis.`
        : `Dites qui débute, écrivez un mot pour chacun·e et le côté pratique, puis transmettez à ${nom ?? 'votre remplaçant·e'}.`;
    case 'atelier_donne':
      return !passee ? 'Après la séance, la personne qui remplace transmet son retour et la liste de présence.'
        : d.retour_transmis ? 'Ouvrez la demande pour lire son retour.'
        : 'La personne qui a remplacé transmet son retour ; vous le lirez dans la demande.';
    default: return '';
  }
}

// ── Urgence et bord de la carte ─────────────────────────────────────────────

export interface UrgenceCarte { joursRestants: number; niveau: NiveauUrgence; urgent: boolean }

/** Une demande pas encore réglée, à venir : son échéance. Rien pour une demande réglée, ou dont la séance est passée. */
export function urgenceDemande(d: Demande, maintenant: Date, seuils: SeuilsUrgence): UrgenceCarte | null {
  if (!STATUTS_NON_RESOLUS.includes(d.statut)) return null;
  const joursRestants = joursAvant(d.date_atelier.slice(0, 10), maintenant);
  if (joursRestants < 0) return null;
  const niveau = niveauUrgence(joursRestants, seuils);
  return { joursRestants, niveau, urgent: niveau === 'urgent' };
}

/** Bord gauche de la carte : rouge = urgent, orange = pas encore réglé, neutre = le reste. Toujours doublé de mots (badge, phrase). */
export type BordCarte = 'urgent' | 'areg' | '';
export function bordCarte(urgence: UrgenceCarte | null): BordCarte {
  return urgence == null ? '' : urgence.urgent ? 'urgent' : 'areg';
}

// ── Les éléments d'une case ─────────────────────────────────────────────────

export type ElementJour =
  | { kind: 'demande'; cle: string; demande: Demande; minutes: number | null }
  | { kind: 'trou'; cle: string; trou: Trou; minutes: number | null };

/** « 14:30:00 » → 870 ; null si inconnue. */
export function heureEnMinutes(t?: string | null): number | null {
  const m = t ? /^(\d{1,2}):(\d{2})/.exec(t) : null;
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Dans la journée, dans l'ordre des heures, comme un agenda (heure inconnue en dernier) ; à heure égale, la séance à couvrir d'abord. */
export function elementsDuJour(demandes: Demande[], trous: Trou[]): ElementJour[] {
  const els: ElementJour[] = [
    ...trous.map((trou): ElementJour => ({ kind: 'trou', cle: `t-${trou.atelierId}-${trou.date}`, trou, minutes: heureEnMinutes(trou.heureDebut) })),
    ...demandes.map((demande): ElementJour => ({ kind: 'demande', cle: `d-${demande.id}`, demande, minutes: heureEnMinutes(demande.atelier_heure_debut) })),
  ];
  const nom = (e: ElementJour) => (e.kind === 'trou' ? e.trou.atelierNom : e.demande.atelier_nom ?? '');
  return els.sort((a, b) =>
    (a.minutes ?? 24 * 60) - (b.minutes ?? 24 * 60)
    || (a.kind === b.kind ? 0 : a.kind === 'trou' ? -1 : 1)
    || nom(a).localeCompare(nom(b), 'fr')
    || a.cle.localeCompare(b.cle));
}
